import React, {useState, useEffect} from 'react'
import { Typography, Box, Stack, Button, Snackbar, Alert, IconButton, Tooltip } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { styled, useTheme } from '@mui/system';
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { apiURL, userURL, storageURL } from './App';
import { authFetch, authJson, authBlob } from './apiClient';
import { FormCard, GrayButton, SecondButton, RoundButton } from './Controls';
import { ThemeInputText, ThemeLabelText, ThemeSelectFile } from './Controls';
import { SelectDate } from './SelectDate';
import { CheckSelect } from './CheckSelect';
import { toDateString } from './Controls';
import { GrayColorButton, SecondColorButton } from './Controls';
import PlaceSelectMap from './PlaceSelectMap';
import PlaceIcon from '@mui/icons-material/Place';
import CloseIcon from '@mui/icons-material/Close';

// 初期表示中心位置
const defaultCenter = {
  lat: parseFloat(process.env.REACT_APP_INIT_LAT || '36.000'),
  lng: parseFloat(process.env.REACT_APP_INIT_LON || '138.000')
};

// コンテナ
const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '30px',
  backgroundColor: '#fff',
  position: 'absolute',
  top: '70px',
  bottom: '0',
  width: '100%',
  height: 'calc(100vh - 70px)'
});

// ファイルURLから File オブジェクト取得（失敗時は null）
async function getFile(url) {
  if (!url) return null;
  // URL 正規化（先頭スラッシュ除去、バックスラッシュをスラッシュに変換）
  const normalized = url.replace(/^\/+/, '').replace(/\\/g, '/');
  // パス分解
  const parts = normalized.split('/');
  // ファイル名部分を取得
  const last = parts.pop() || 'file';
  // ファイル名デコード
  let fileName;
  try { fileName = decodeURIComponent(last); } catch { fileName = last; }
  // ファイル名のみ再度エンコードし、URL生成（他セグメントは ASCII 前提）
  const encodedFileName = encodeURIComponent(fileName);
  const fullUrl = `${storageURL}/${parts.join('/')}/${encodedFileName}`;
  try {
    // API呼び出し
    const blob = await authBlob(fullUrl);
    // BlobからFileオブジェクトを生成
    return new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
  }
  catch {
    // 取得失敗
    return null;
  }
}

// 削除ボタン
const DeleteButton = (props) => {
    const [open, setOpen] = useState(false); // 確認ダイアログの表示/非表示

    // 確認ダイアログ表示
    const handleOpen = () => {
      setOpen(true);
    };
  
    // 確認ダイアログ非表示
    const handleClose = () => {
      setOpen(false);
    };

    // 削除ボタン
    const handleDelete = (e) => {
      // 確認ダイアログ非表示
      setOpen(false)
      // 親の削除イベント
      props.onClick(e)
    };

    // ボタンレンダリング    
    return (
      <>
        <RoundButton onClick={handleOpen} variant="contained" color="error">{props.children}</RoundButton>
        <Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>メモの削除</Typography>
          </DialogTitle>
          <DialogContent sx={{pr:20}}>
            <DialogContentText id="alert-dialog-description">
              <Typography variant="inherit">このメモを削除しますか？</Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{p:2}}>
            <Button onClick={handleClose} variant="contained" style={{color: 'white', backgroundColor: 'gray'}} autoFocus>キャンセル</Button>
            <Button onClick={handleDelete} variant="contained" color="error">削除</Button>
          </DialogActions>
        </Dialog>        
      </>
    );
};

// メモ詳細ページ
const MemoDetailsPage = (props) => {
  const theme = useTheme();
  const { control, register, handleSubmit, setValue, getValues, reset } = useForm();
  const navigate = useNavigate();
  const [ userName, setUserName ] = useState("");
  const [ genres, setGenres ] = useState([]);
  const [ details, setDetails ] = useState({id:props.id});
  const [ files, setFiles ] = useState([]);
  const [ fileError, setFileError ] = useState('');
  const [ fileErrorOpen, setFileErrorOpen ] = useState(false);
  const [ point, setPoint ] = useState(null);
  const [ mapOpen, setMapOpen ] = useState(false);
  const [ mapDraftLocation, setMapDraftLocation ] = useState(null);

  // form参照用ref
  const formRef = React.useRef(null);

  // 初期表示
  useEffect(() => {
    // 並列取得
    Promise.all([
      // ユーザ名取得
      fetchUserName(),
      // ジャンルリスト取得
      fetchGenreList(),
    ])
    .then(() => {
      // 編集の場合のみ fetchDetails をジャンル取得後に呼ぶ
      if (props.mode === "Edit") {
        fetchDetails();
      }
    });
  }, [props])

  // 地図検索
  const onSearchEnter = (obj) => {
    // 検索結果位置情報
    //setSearchResult(obj);
    // 位置情報設定
    setPoint({
      name: obj.properties.name, 
      lat: obj.properties.coordinates.latitude, 
      lon: obj.properties.coordinates.longitude
    })
    // 場所を設定
    setValue('place', obj.properties.name)
  };

  // キャンセル
  const onClickCancel = (e) => {
    // １つ前に戻る
    navigate(-1);
  };

  // 削除ボタン
  const onDeleteClick = () => {
    // リクエストデータ作成
    const data = {id: props.id}
    // ユーザ情報が存在する場合
    if (props.user != null) {
      data.change_account = props.user.username;
    }
    // フォームデータ追加
    const body = new FormData()
    body.append("props", JSON.stringify(data))
    // メモデータ削除API呼び出し
    authFetch(`${apiURL}/memos/delete`, { method: 'POST', body, actionName: 'RemoveMemo' })
      .then(() => {
        // 検索へ
        navigate('/memo/search', { replace: true });
      })
      .catch(e => {
        // 削除エラー
        console.log(e);
      });
  };

  // ユーザ名取得
  const fetchUserName = async () => {
    try {
      // API呼び出し
      const data = await authJson(`${userURL}/${props.user.id}/fullname/`);
      // ユーザ名設定
      setUserName(data);
    }
    catch(e) {
      // 取得エラー
      console.log(e);
      // ユーザ名初期化
      setUserName('');
    }
  }

  // ジャンルリスト取得
  const fetchGenreList = async () => {
    try {
      // API呼び出し
      //const data = await authJson(`${apiURL}/master/genres`);
      const res = await fetch("/memo_genres.json");
      const data = await res.json();
      if (!Array.isArray(data)) {
        throw new Error('memo_genres.json unexpected format: ' + JSON.stringify(data));
      }
      // ジャンルリスト設定
      setGenres(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // ジャンルリスト初期化
      setGenres([]);
    }
  }

  // メモデータ取得
  const fetchDetails = async () => {
    // メモデータとファイルリスト初期化
    let newDetails = {id:props.id}
    let newFiles = []
    // メモデータ検索
    try {
      // API呼び出し
      const data = await authJson(`${apiURL}/memos/${props.id}`);
      console.log(data)
      // メモデータがある場合
      if (data != null) {
        // メモデータ設定
        newDetails = data
        // ジャンルid配列初期化
        data.genre_ids = [];
        // ジャンル文字列がある場合
        if (data.genre_text != null && data.genre_text !== "") {
          // カンマセパレータ区切りで分割し、前後の空白を除去した名前配列を作成
          const genre_names = data.genre_text.split(',').map(s => s.trim());
          // 名前配列をid配列に変換
          const genre_ids = genre_names.map(name => {
            const genre = genres.find(g => g.name === name);
            return genre ? genre.id : null;
          });
          // ジャンルid配列設定
          data.genre_ids = genre_ids;
        }
        // メモデータ項目数分
        for (let name in data) {
          // ユーザ名とファイルリスト以外をフォーム値へ設定
          if (name != "user_full_name" && name != "files") {
            setValue(name, data[name]);
          }
        }
        // ユーザ名が設定されている場合
        if (data.user_full_name != null) {
          // ユーザ名設定
          setUserName(data.user_full_name)
        }
        // ファイルリスト作成
        if (data.files != null) {
          // すべてのファイルを取得
          const fetched = await Promise.all(
            data.files.map(src => getFile(src).catch(() => null))
          );
          // null でないものだけ抽出
          newFiles = fetched.filter(f => !!f);
        }
        // 緯度経度が設定されている場合
        if (data.lon != null && data.lat != null) {
          // 位置情報設定
          setPoint({name: data.place, lat: data.lat, lon: data.lon})
        }
      }
    }
    catch (e) {
      // 取得エラー
      console.log(e);
    }
    // メモデータ設定
    setDetails(newDetails);
    // ファイルリスト設定
    setFiles(newFiles);
  }

  // メモ新規登録・更新
  const registData = async (data) => {
    // 位置情報が存在する場合
    if (point != null) {
      data.lat = point.lat;
      data.lon = point.lon;
    } else {
      data.lat = null;
      data.lon = null;
    }    
    // ユーザ情報が存在する場合
    if (props.user != null) {
      // ユーザID
      data.user_id = props.user.id;
      // ユーザ名
      if (props.mode == "New") {  // 新規
        data.entry_account = props.user.username;
      }else {                     // 編集
        data.change_account = props.user.username;
      }
    }
    // ジャンルid配列がある場合
    if (Array.isArray(data.genre_ids)) {
      // id配列を名前配列に変換
      const genre_names = data.genre_ids.map(id => {
        const genre = genres.find(g => g.id === id);
        return genre ? genre.name : null;
      });
      // 名前配列をカンマセパレータ区切り文字列に変換して設定
      data.genre_text = genre_names.filter(name => name).join(', ');
    }
    // ジャンルid配列を削除
    delete data['genre_ids'];
    // 日付フォーマット
    data.target_date = toDateString(data.target_date);
    // ファイル操作ありを通知
    data.is_files = true
    // フォームデータ追加
    const body = new FormData()
    body.append("props", JSON.stringify(data))
    for (let file of files) {
      body.append("files", file)
    }
    console.log(...body.entries());
    // メモ新規登録・更新API呼び出し
    let url = ""
    if (props.mode == "New") {  // 新規
      url = `${apiURL}/memos/create`
    }else {                     // 編集
      url = `${apiURL}/memos/update`
    }
    authFetch(url, { method: 'POST', body, actionName: 'RegistMemo' })
      .then(() => {
        // 新規登録
        if (props.mode == 'New') { 
          // 検索へ
          navigate('/memo/search', { replace: true });
        }
        // 編集
        else {
          // 再表示のためにリロードフラグを設定して戻る
          sessionStorage.setItem('reload', 'on');
          navigate(-1); // 戻る
        }
      })
      .catch(e => {
        // 更新エラー
        console.log(e);
      });
  }

  // 地図で位置が選択されたときの処理
  const handleMapPlaceSelected = (place) => {
    console.log('検索で選択:', place)

    // 位置設定がない場合
    if(!place){
      // 位置クリア
      setMapDraftLocation(null)
      return
    }
    // 位置設定
    place.event = "suggestion"   // 検索で選択されたことを示すフラグを追加
    setMapDraftLocation(place)
  }

  // 地図位置変更(クリックでマーカー変更)
  function handleMapMarkerChange(marker){
    console.log('クリックでマーカー変更:', marker)

    // 位置設定がない場合
    if(!marker){
      // 位置クリア
      setMapDraftLocation(null)
      return
    }
    // 位置設定
    marker.event = "click"    // クリックで変更されたことを示すフラグを追加
    setMapDraftLocation(marker)
  }

  // 地図ダイアログ表示
  const handleMapDlgOpen = () => {
    // ダイアログ用の位置を設定（現在の位置 or null）
    setMapDraftLocation(point ? {lat:point.lat, lng:point.lon, name:point.name} : null);     
    // ダイアログ表示
    setMapOpen(true);
  }

  // 地図ダイアログでOKが押されたときの処理
  const handleMapDlgOK = async () => {
    // ダイアログを閉じる
    setMapOpen(false);

    // 位置がない場合
    if(!mapDraftLocation) {
      // 位置削除
      setPoint(null);
      setValue('place', '', { shouldDirty: true });
      return;
    }

    // 検索バーから選択された位置の場合
    if (mapDraftLocation.event === "suggestion") {
      // 位置設定
      setPoint({lat:mapDraftLocation.lat, lon:mapDraftLocation.lng, name:mapDraftLocation.name});
      setValue('place', mapDraftLocation.name || '', { shouldDirty: true });
      return;
    }

    // Google Maps API ロードチェック
    if (!window.google?.maps?.places) {
      console.error("Google Maps API not loaded");
      return;
    } 
    // Google Maps API の Geocoder と PlacesService を初期化
    const geocoder = new window.google.maps.Geocoder();
    const service = new window.google.maps.places.PlacesService(document.createElement("div"));    
    const latLng = new window.google.maps.LatLng(mapDraftLocation.lat, mapDraftLocation.lng);

    // 1. PlaceId がある場合は getDetails
    const getPlaceNameFromPlaceId = () => {
      return new Promise((resolve) => {
        if (!mapDraftLocation.placeId) return resolve(null);
        service.getDetails(
          { placeId: mapDraftLocation.placeId, fields: ['geometry', 'name', 'formatted_address'] },
          (place, status) => {
            if (status === "OK" && place) {
              console.log("getPlaceNameFromPlaceId", place)
              resolve({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng(), name: place.name });
            } else {
              console.warn("getPlaceNameFromPlaceId failed:", status);
              resolve(null);
            }
          }
        );
      });
    };

    // 2. NearbySearch
    const getPlaceNameFromNearby = () => {
      return new Promise((resolve) => {
        service.nearbySearch({ location: latLng, radius: 30 }, (res, status) => {
          if (status === "OK" && res.length > 0) {
            console.log("getPlaceNameFromNearby", res[0])
            resolve({ lat: mapDraftLocation.lat, lng: mapDraftLocation.lng, name: res[0].name });
          } else {
            console.warn("getPlaceNameFromNearby failed:", status);
            resolve(null);
          }
        });
      });
    };

    // 3. 逆ジオコーディング
    const getPlaceNameFromGeocode = () => {
      // 名称取得
      const getName = (result) => {
        // formatted_addressじゃ日本か郵便番号まで入ってくるので除去
        let addr = result.formatted_address.replace(/^日本、?|〒\d{3}-\d{4}\s*/g, '');
        return addr;
      };
      // 逆ジオコーディング実行
      return new Promise((resolve) => {
        geocoder.geocode({ location: latLng }, (res, status) => {
          if (status === "OK" && res.length > 0) {
            console.log("getPlaceNameFromGeocode", res[0])
            resolve({ lat: mapDraftLocation.lat, lng: mapDraftLocation.lng, name: getName(res[0]) });
          } else {
            console.warn("getPlaceNameFromGeocode failed:", status);
            resolve({ lat: mapDraftLocation.lat, lng: mapDraftLocation.lng, name: null });
          }
        });
      });
    };

    // 位置名称の取得を実行
    let place = await getPlaceNameFromPlaceId();
    //if (!place) place = await getPlaceNameFromNearby();  // 名称かなりの確率で取得できるが課金対象APIなので注意
    if (!place) place = await getPlaceNameFromGeocode();
    console.log("取得した位置情報:", place)

    // 位置設定
    setPoint({lat:place.lat, lon:place.lng, name:place.name});
    setValue('place', place.name || '', { shouldDirty: true });
  }

  // レンダリング
  return (
    <>
    <Container>
      <Box sx={{ width: '100%', maxWidth: 1000, minWidth: 500 }}>
        <FormCard sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>メモ詳細</Typography>
            <Typography variant="inherit" color="text.disabled">メモの詳細を入力してください。</Typography>
          </Box>
          <Box component="form" onSubmit={handleSubmit(registData)} sx={{ mt: 3, flexGrow: 1, overflowY: 'auto', pr: 1, pb: 2 }} ref={formRef}>
            <Stack direction="row" spacing={4} alignItems="flex-start">
              {/* 左列 */}
              <Stack direction="column" spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                <ThemeLabelText label="名前" placeholder="ユーザ名" value={userName}/>
                <SelectDate id="target_date" label="日付" placeholder="日付を入力" control={control}/>
                <ThemeInputText
                  id="place"
                  label="場所"
                  placeholder="場所を入力"
                  control={control}
                  endAdornment={
                    <Tooltip title="地図を表示 / 位置を設定">
                      <IconButton size="medium" onClick={handleMapDlgOpen} aria-label="select location">
                        <PlaceIcon fontSize="small" color="primary"/>
                      </IconButton>
                    </Tooltip>
                  }
                />
                <ThemeInputText id="contents" label="本文" placeholder="本文を入力" multiline rows={3} control={control}/>
                <ThemeSelectFile
                  id="detail-files"
                  label="添付ファイル（任意）"
                  placeholder="添付ファイルをアップロード"
                  files={files}
                  setFiles={(f) => { setFiles(f); }}
                  accept="image/*,application/pdf,text/*,.txt,.csv,.json,.md,.log"
                  maxFiles={20}
                  maxSizeMB={50}
                  onError={(msg) => { setFileError(msg); setFileErrorOpen(true); }}
                />
              </Stack>
              {/* 右列（本文・備考） */}
              <Stack direction="column" spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                <CheckSelect id="genre_ids" label="ジャンル" placeholder="ジャンルをプルダウンから選択" value={getValues("genre_ids")} options={genres} register={register}/>
                <ThemeInputText id="remarks" label="備考" placeholder="備考を入力" multiline rows={6} control={control}/>
                {props.mode !== "New" && (
                  <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" sx={{ pt: 2, width: '100%' }}>
                    <DeleteButton onClick={onDeleteClick}>メモを削除</DeleteButton>
                  </Box>
                )}
              </Stack>
            </Stack>
          </Box>
          {/* アクションバー（flex下寄せ） */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
            <GrayButton variant="contained" onClick={onClickCancel}>キャンセル</GrayButton>
            <SecondButton variant="contained" onClick={() => formRef.current && formRef.current.requestSubmit()}>保存</SecondButton>
          </Box>
        </FormCard>
      </Box>
    </Container>
    {/* 地図ダイアログ */}
    <Dialog open={mapOpen} onClose={() => setMapOpen(false)} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', pr:1 }}>
        <Box sx={{ flex:1, textAlign:'left', fontSize:16, fontWeight:'bold' }}>位置を選択</Box>
        <Box sx={{ flex:1, textAlign:'center' }}>
          <Button size="medium" color="error" onClick={() => setMapDraftLocation(null)} disabled={!mapDraftLocation}>
            選択中の位置を削除
          </Button>
        </Box>
        <Box sx={{ flex:1, display:'flex', justifyContent:'flex-end' }}>
          <IconButton size="small" onClick={() => setMapOpen(false)} aria-label="close"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p:0 }}>
        <Box sx={{ width:'100%', textAlign:'left', pl:2, pt:0, minHeight:0 }}>
          <Typography variant="caption" sx={{ color:'red', fontSize:'0.82rem', fontWeight:400, lineHeight:1 }}>
            ※地図検索で無料枠を超えた場合、課金が発生します
          </Typography>
        </Box>
        <Box sx={{ height: '60vh', position:'relative' }}>
          <PlaceSelectMap
            initialMarker={mapDraftLocation}
            defaultCenter={defaultCenter}
            onPlaceSelected={handleMapPlaceSelected}
            onMarkerChange={handleMapMarkerChange}
            countryRestriction="JP"
          />          
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent:'flex-end' }}>
        <GrayColorButton onClick={() => { setMapOpen(false); }}>キャンセル</GrayColorButton>
        <SecondColorButton onClick={handleMapDlgOK}>
          OK
        </SecondColorButton>
      </DialogActions>
    </Dialog>
    {/* ファイル関連エラートースト */}
    <Snackbar
      open={fileErrorOpen}
      autoHideDuration={4000}
      onClose={(e, reason) => { if (reason === 'clickaway') return; setFileErrorOpen(false); setFileError(''); }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Alert onClose={() => { setFileErrorOpen(false); setFileError(''); }} severity="warning" variant="filled" sx={{ width: '100%' }}>
        {fileError}
      </Alert>
    </Snackbar>
    </>       
  );
};

export default MemoDetailsPage;
